import moment from "moment-timezone";
import { inject, Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { of } from "rxjs";
import { patch } from "@ngxs/store/operators";
import { tap } from "rxjs/operators";
import { BookingApiService, CheckoutMode, DoorStatus } from "@3volutionsag/ngx-rooms-lib";

import { BookingFreeBusy } from "../booking-freebusy.enum";
import { LedApiService } from "../../led/led-api.service";
import { ConfigurationState } from "../../configuration/state/configuration.state";
import { BookingService } from "../booking.service";
import { HttpRequestHelperService } from "../../shared/http-request-helper.service";

import {
	CheckinBooking,
	CheckoutBooking,
	LoadBookingsForDay,
	LoadBookingsOfCurrentDay,
	RecalculateRoomAvailabilityStatus,
} from "./booking.actions";

import type { BookingModel, RequestOptions } from "@3volutionsag/ngx-rooms-lib";
import type { ConfigurationStateModel } from "../../configuration/state/configuration.state";
import type { Observable } from "rxjs";
import type { NgxsAfterBootstrap } from "@ngxs/store";

export interface BookingStateModel {
	canCheckIn: boolean;
	canCheckOut: boolean;
	isPreparation: boolean;
	isFollowUp: boolean;
	freeBusy: BookingFreeBusy;
	nextBookings?: BookingModel[];
	currentBooking?: BookingModel;
	// NOTE: Do not use Map<> here since a map is not serializable while a object is
	bookingsForDay: Record<number | "current", BookingModel[]>;
}

@State<BookingStateModel>({
	name: "booking",
	defaults: {
		canCheckIn: false,
		canCheckOut: false,
		isPreparation: false,
		isFollowUp: false,
		nextBookings: undefined,
		currentBooking: undefined,
		freeBusy: BookingFreeBusy.Free,
		bookingsForDay: {
			current: [],
		},
	},
})
@Injectable()
export class BookingState implements NgxsAfterBootstrap {
	private bookingApiService = inject(BookingApiService);
	private httpRequestHelper = inject(HttpRequestHelperService);

	constructor(
		private store: Store,
		private ledApiService: LedApiService,
		private bookingService: BookingService,
	) {}

	@Selector()
	public static bookingsForDay(state: BookingStateModel) {
		return state.bookingsForDay;
	}

	@Selector([BookingState.bookingsForDay])
	public static bookingsOfCurrentDay(
		bookingsForDay: BookingStateModel["bookingsForDay"],
	): BookingModel[] {
		return bookingsForDay.current;
	}

	@Selector([BookingState.bookingsForDay])
	public static bookingsOfDay(bookingsForDay: BookingStateModel["bookingsForDay"]) {
		return (day: moment.Moment) => {
			if (day.isSame(moment(), "day")) {
				return bookingsForDay.current;
			}

			const dayIndex = this.getDayIndex(day);
			if (Object.prototype.hasOwnProperty.call(bookingsForDay, dayIndex)) {
				return bookingsForDay[dayIndex];
			}

			return [];
		};
	}

	@Selector()
	public static canCheckIn(state: BookingStateModel): boolean {
		return state.canCheckIn;
	}

	@Selector()
	public static canCheckOut(state: BookingStateModel): boolean {
		return state.canCheckOut;
	}

	@Selector([BookingState.currentBooking])
	public static isBlockingTime(
		currentBooking: BookingStateModel["currentBooking"],
	): boolean | undefined {
		return currentBooking?.isBlockingTime;
	}

	@Selector([BookingState.currentBooking])
	public static isPrivateBooking(
		currentBooking: BookingStateModel["currentBooking"],
	): boolean | undefined {
		return currentBooking?.istPrivat;
	}

	@Selector([BookingState.currentBooking])
	public static isRunningBooking(currentBooking: BookingStateModel["currentBooking"]): boolean {
		if (!currentBooking) {
			return false;
		}

		const now = moment();
		// round up to the next minute
		const roundedNow = now.clone().add(1, "minutes");
		return (
			currentBooking.beginPreparation.isSameOrBefore(now) &&
			currentBooking.endFollowUp.isAfter(roundedNow)
		);
	}

	@Selector()
	public static isPreparation(state: BookingStateModel): boolean {
		return state.isPreparation;
	}

	@Selector()
	public static isFollowUp(state: BookingStateModel): boolean {
		return state.isFollowUp;
	}

	@Selector()
	public static currentBooking(state: BookingStateModel): BookingModel | undefined {
		return state.currentBooking;
	}

	@Selector()
	public static nextBookings(state: BookingStateModel): BookingModel[] | undefined {
		return state.nextBookings;
	}

	@Selector()
	public static freeBusy(state: BookingStateModel): BookingFreeBusy {
		return state.freeBusy;
	}

	ngxsAfterBootstrap(): void {
		// Note: Maybe we have to move this since it is technically possible that we do not yet have a configuration in store
		this.bookingService.initBookingFetchInterval();
		this.bookingService.initStatusUpdateInterval();
	}

	@Action(LoadBookingsOfCurrentDay)
	public loadBookingsOfCurrentDay(ctx: StateContext<BookingStateModel>) {
		return this.loadBookingsForSpecificDay(moment()).pipe(
			tap((bookings) => {
				ctx.setState(
					patch<BookingStateModel>({
						bookingsForDay: patch<BookingStateModel["bookingsForDay"]>({
							current: bookings,
						}),
					}),
				);
				this.store.dispatch(new RecalculateRoomAvailabilityStatus());
			}),
		);
	}

	@Action(LoadBookingsForDay)
	public loadBookingsForDay(ctx: StateContext<BookingStateModel>, action: LoadBookingsForDay) {
		const dayIndex = BookingState.getDayIndex(action.day);
		return this.loadBookingsForSpecificDay(action.day).pipe(
			tap((bookings) => {
				let patchState: Partial<BookingStateModel["bookingsForDay"]>;
				if (action.day.isSame(moment(), "day")) {
					patchState = {
						current: bookings,
					};
				} else {
					patchState = {
						[dayIndex]: bookings,
					};
				}

				ctx.setState(
					patch<BookingStateModel>({
						bookingsForDay: patch<BookingStateModel["bookingsForDay"]>(patchState),
					}),
				);
				this.store.dispatch(new RecalculateRoomAvailabilityStatus());
			}),
		);
	}

	@Action(RecalculateRoomAvailabilityStatus)
	public recalculateRoomAvailabilityStatus(ctx: StateContext<BookingStateModel>): void {
		const state = ctx.getState();
		const configState = this.store.selectSnapshot<ConfigurationStateModel>(
			ConfigurationState.state,
		);

		const now = moment();
		let canCheckIn = false;
		let canCheckOut = false;
		let isPreparation = false;
		let isFollowUp = false;
		let bookingToShow: BookingModel | undefined;
		let freeBusy: BookingFreeBusy = BookingFreeBusy.Free;
		const sortedBookings = [...state.bookingsForDay.current].sort((a, b) => {
			const blockCmp = Number(!!a.isBlockingTime) - Number(!!b.isBlockingTime);
			if (blockCmp !== 0) return blockCmp;
			return a.begin.valueOf() - b.begin.valueOf();
		});

		const upcomingBookings = sortedBookings.filter((b) => b.end.isAfter(now));

		for (const booking of upcomingBookings) {
			// LOGIC: 1) Is there a currently running booking?
			if (BookingState.isRunningBooking(booking)) {
				bookingToShow = booking;
				canCheckIn = this.canCheckinBooking(booking);
				canCheckOut = this.canCheckoutBooking(booking, configState);
				isPreparation = booking.begin.isAfter(now) && booking.beginPreparation.isBefore(now);
				isFollowUp = booking.end.isBefore(now) && booking.endFollowUp.isAfter(now);

				if (booking.isBlockingTime) {
					freeBusy = BookingFreeBusy.BlockingTime;
				} else if (configState.dsConfiguration?.checkin === false) {
					freeBusy = BookingFreeBusy.Busy;
				} else {
					freeBusy = bookingToShow.checkinConfirmed
						? BookingFreeBusy.Busy
						: BookingFreeBusy.CheckinRequired;
				}

				break;
			}

			// LOGIC: 2) Is there a upcoming booking within the checkin time?
			if (configState.dsConfiguration?.checkin && booking.checkinLeadTime) {
				if (booking.begin.isAfter(now) && now.isAfter(booking.checkinLeadTime)) {
					bookingToShow = booking;
					canCheckIn = this.canCheckinBooking(booking);
					freeBusy = bookingToShow.checkinConfirmed
						? BookingFreeBusy.Busy
						: BookingFreeBusy.CheckinRequired;
					break;
				}
			}
		}

		switch (freeBusy) {
			case BookingFreeBusy.BlockingTime:
			case BookingFreeBusy.Busy:
				this.ledApiService.set("RED");
				break;
			case BookingFreeBusy.CheckinRequired:
				this.ledApiService.set("NEUTRAL");
				break;
			case BookingFreeBusy.Free:
				this.ledApiService.set("GREEN");
				break;
		}

		ctx.patchState({
			freeBusy,
			canCheckIn,
			canCheckOut,
			isFollowUp,
			isPreparation,
			currentBooking: bookingToShow,
			nextBookings: bookingToShow
				? upcomingBookings.filter((b) => b.id !== bookingToShow?.id)
				: upcomingBookings,
		});
	}

	@Action(CheckinBooking)
	public checkIn(ctx: StateContext<BookingStateModel>) {
		const state = ctx.getState();

		if (!state.currentBooking || !state.canCheckIn) {
			return;
		}

		const dsConfig = this.store.selectSnapshot(ConfigurationState.dsConfiguration);
		if (dsConfig?.unauthorizedActions ?? true) {
			this.bookingApiService.onExecuteFn = (reqOptions?: RequestOptions) =>
				this.httpRequestHelper.prepareRequest(true, reqOptions);
		}

		return this.bookingApiService.checkIn(state.currentBooking?.id).pipe(
			tap((booking) => {
				ctx.patchState({
					currentBooking: booking,
				});
				this.store.dispatch(new LoadBookingsOfCurrentDay());
			}),
		);
	}

	@Action(CheckoutBooking)
	public checkOut(ctx: StateContext<BookingStateModel>) {
		const state = ctx.getState();

		if (!state.currentBooking || !state.canCheckOut) {
			return;
		}

		const dsConfig = this.store.selectSnapshot(ConfigurationState.dsConfiguration);
		if (dsConfig?.unauthorizedActions ?? true) {
			this.bookingApiService.onExecuteFn = (reqOptions?: RequestOptions) =>
				this.httpRequestHelper.prepareRequest(true, reqOptions);
		}

		return this.bookingApiService
			.checkOut(state.currentBooking?.id, dsConfig?.checkoutMode === CheckoutMode.Terminate)
			.pipe(
				tap((booking) => {
					ctx.patchState({
						currentBooking: booking,
					});
					this.store.dispatch(new LoadBookingsOfCurrentDay());
				}),
			);
	}

	private static getDayIndex(day: moment.Moment): number {
		return day.clone().startOf("D").unix();
	}

	private loadBookingsForSpecificDay(day: moment.Moment): Observable<BookingModel[]> {
		const configState = this.store.selectSnapshot<ConfigurationStateModel>(
			ConfigurationState.state,
		);
		if (!configState.dsConfiguration) {
			return of([]);
		}

		this.bookingApiService.onExecuteFn = (reqOptions?: RequestOptions) =>
			this.httpRequestHelper.prepareRequest(true, reqOptions);

		const endOfDay = day.clone().endOf("D");
		const startOfDay = day.clone().startOf("D");

		return this.bookingApiService.getDoorsignBookings(
			configState.dsConfiguration.resourceId,
			startOfDay,
			endOfDay,
		);
	}

	private canCheckinBooking(booking: BookingModel): boolean {
		if (booking && booking.doorman) {
			return booking.doorman.doorStatus === DoorStatus.Closed;
		}

		if (booking.isBlockingTime || booking.checkinConfirmed) {
			return false;
		}

		return (booking.checkinLeadTime && booking.checkinLeadTime.isSameOrBefore(moment())) ?? false;
	}

	private canCheckoutBooking(booking: BookingModel, configState: ConfigurationStateModel): boolean {
		if (booking && booking.doorman) {
			return booking.doorman.doorStatus === DoorStatus.Open;
		}

		if (
			configState.dsConfiguration?.checkoutMode === CheckoutMode.Terminate &&
			booking.end.isBefore(moment())
		) {
			return false;
		}

		return configState.dsConfiguration?.checkin && booking.checkinConfirmed ? true : false;
	}
}
